import React from "react";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="py-16 md:py-24" data-testid="privacy-page">
      <div className="container-tight max-w-3xl">
        <div className="overline">Legal</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-3 leading-[1.02]">
          Privacy <span className="text-primary">policy</span>.
        </h1>
        <p className="mt-6 text-foreground/60 text-sm">Last updated: 27 July 2026</p>

        <div className="mt-8 border-l-2 border-secondary bg-secondary/10 p-5 text-sm text-foreground/80">
          <span className="font-semibold">Template.</span> This is a working draft written in the site&apos;s voice.
          Please have it reviewed by a qualified legal professional before treating it as your final published policy.
        </div>

        <div className="mt-12 space-y-10 text-foreground/80 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl md:text-3xl mb-4 text-foreground">1. Who we are</h2>
            <p>
              The TGI Golfer Fund (&quot;we&quot;, &quot;us&quot;) is operated by Talk Golf International, Dublin,
              Ireland. We are the data controller for personal data collected through this website. You can reach
              us at{" "}
              <a href="mailto:golfersfund@talkgolfireland.com" className="text-primary underline">
                golfersfund@talkgolfireland.com
              </a>{" "}
              or on +353 87 425 7257.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl mb-4 text-foreground">2. What we collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><span className="font-semibold">Account data.</span> Name, email, password hash, role (golfer / supporter / sponsor / admin), profile image URL.</li>
              <li><span className="font-semibold">Application data.</span> Country, age, handicap, golf club or business, ranking, story, funding goal, references, social links.</li>
              <li><span className="font-semibold">Transaction data.</span> Donations and sponsorships you make, including amount, tier, message and whether the donation is anonymous.</li>
              <li><span className="font-semibold">Google sign-in data.</span> If you sign in with Google, we receive your email, name and profile picture from Google.</li>
              <li><span className="font-semibold">Technical data.</span> Session cookies to keep you signed in, IP-derived rate-limiting data for security.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl mb-4 text-foreground">3. Why we process it</h2>
            <p>Under GDPR Article 6, our legal bases for processing are:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><span className="font-semibold">Contract.</span> Running your account, taking donations, publishing your golfer profile.</li>
              <li><span className="font-semibold">Legitimate interest.</span> Verifying applications, preventing fraud, keeping the platform secure.</li>
              <li><span className="font-semibold">Consent.</span> Optional cookies and any future marketing communications.</li>
              <li><span className="font-semibold">Legal obligation.</span> Accounting and tax records.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl mb-4 text-foreground">4. Who we share it with</h2>
            <p>We never sell your data. Limited categories of third parties process data on our behalf:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Google (when you use &quot;Continue with Google&quot;)</li>
              <li>Our payment processor (once real payments go live) for donations and sponsorships</li>
              <li>Cloud hosting and database providers used to run the platform</li>
            </ul>
            <p className="mt-3">Where transfers outside the EEA are involved, we rely on Standard Contractual Clauses.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl mb-4 text-foreground">5. How long we keep it</h2>
            <p>
              Account data is kept while your account is active and deleted within 90 days of closure. Transaction
              records are retained for six years to meet Irish accounting obligations. Rejected applications are
              deleted within 12 months unless you ask us to keep them for a future review.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl mb-4 text-foreground">6. Your rights</h2>
            <p>You can, at any time:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Access the personal data we hold on you</li>
              <li>Ask us to correct anything inaccurate</li>
              <li>Ask us to erase your data (subject to legal retention rules)</li>
              <li>Ask for a portable copy of your data</li>
              <li>Object to processing based on legitimate interest</li>
              <li>Withdraw consent for anything you consented to</li>
              <li>Complain to the Irish Data Protection Commission (dataprotection.ie)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl mb-4 text-foreground">7. Cookies</h2>
            <p>
              See our <Link to="/cookies" className="text-primary underline">Cookie policy</Link> for the full
              breakdown of what we set and why.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl md:text-3xl mb-4 text-foreground">8. Changes</h2>
            <p>
              We&apos;ll update this page when our practices change and put the new &quot;last updated&quot; date at
              the top. For material changes we&apos;ll email registered users.
            </p>
          </section>
        </div>

        <div className="mt-16 border-t border-border pt-8 flex flex-wrap gap-3">
          <Link to="/" className="btn-outline uppercase tracking-[0.18em] text-[12px]" data-testid="privacy-home-link">Back to home</Link>
          <Link to="/cookies" className="btn-outline uppercase tracking-[0.18em] text-[12px]">Cookie policy</Link>
          <Link to="/terms" className="btn-outline uppercase tracking-[0.18em] text-[12px]">Terms</Link>
        </div>
      </div>
    </div>
  );
}
